import React from "react";
import {
    IProtocolStep, IProtocolStepKind,
} from "../../../types/protocol";
import {TProtocolStepId} from "../../../types/protocol";
import {TProtocolStepEventId} from "../../../types/IProtocolStepEvent";
import {IProtocolStepEvent} from "../../../types/IProtocolStepEvent";
import {Box, ActionIcon, Button, Group, Tooltip} from "@mantine/core";
import {
    IconEdit,
    IconAlertTriangle,
} from "@tabler/icons-react";
import {StepView, TWorkflow} from "@/protocols/common/steps/StepView";
import cx from "clsx";
import classes from "./StepRow.module.css";
import {StepTitleBar} from "@/protocols/common/steps/views/components/StepTitleBar.tsx";
import {TimingWarning} from "@/protocols/studies/review/checkStepTiming";
import {EventListView} from "@/protocols/common/components/EventListView";
import {DeepPartial} from "@/utils/types/DeepPartial.ts";
import {Instructions} from "@/protocols/common/steps/fields";

interface StepRowProps {
    step: IProtocolStep;
    events: Array<IProtocolStepEvent>;
    isGroupMember: boolean;
    workflow: TWorkflow;
    onEdit: (step: IProtocolStep) => void;
    onChange: (stepId: TProtocolStepId, updates: DeepPartial<IProtocolStep>) => void;
    onCancel: (stepId: TProtocolStepId) => void;
    onUpdate: (stepId: TProtocolStepId) => void;
    timingWarning?: TimingWarning;
    /** Map of event ID -> warning message for events outside step boundaries */
    eventWarnings?: Map<TProtocolStepEventId, string>;
    /** Timezone for displaying event timestamps */
    timezone?: string;
    readOnly?: boolean
}

/**
 * Renders a protocol step in review mode with StepsView-like header
 * and detailed content below. Matches StepsView layout for step rows.
 * Uses the same StepView component as protocol recording.
 */
export const StepRow: React.FC<StepRowProps> = ({
                                                    step,
                                                    events,
                                                    isGroupMember,
                                                    workflow,
                                                    onEdit,
                                                    onChange,
                                                    onCancel,
                                                    onUpdate,
                                                    timingWarning,
                                                    eventWarnings,
                                                    readOnly,
                                                    timezone = "UTC",
                                                }) => {
    const isEditing = workflow === "review_edit";
    const isGroup = step.kind === "group";
    const handleOnChange = (updates: DeepPartial<IProtocolStep>) => {
        onChange(step.id, updates)
    }
    const renderInstructionView = () => {
        if (step.instructions && step.kind !== IProtocolStepKind.INSTRUCTIONS && step.kind !== IProtocolStepKind.GROUP) {
            return (<Instructions instructions={step.instructions || ""} editingMode="none"
                                  onChange={() => {}}/>)
        } else {
            return (<></>)
        }
    }

    return (
        <Box
            className={cx(classes.stepRow, {
                [classes.groupMember]: isGroupMember,
                [classes.groupHeader]: isGroup
            })}
        >
            {/* Header row matching StepsView: icon | name | end_time | status | edit */}
            <Box style={{display: "grid", grid: "auto / 1fr auto", alignItems: "center"}}>
                <Box style={{display: "flex", alignItems: "center", gap: "0.25rem"}}>
                    {timingWarning && (
                        <Box ml="0.25em" mt={"0.25em"} style={{alignSelf: "center"}}>
                            <Tooltip label={timingWarning.reason}>
                                <IconAlertTriangle size={"1.5em"} color={"var(--bios-color-warning)"}/>
                            </Tooltip>
                        </Box>
                    )}
                    <StepTitleBar step={step} onChange={handleOnChange} workflow={workflow}/>
                </Box>
                {step.kind !== IProtocolStepKind.GROUP && !readOnly ?
                    <Box className={classes.editButton} mt={"1em"}>
                        <ActionIcon
                            variant="subtle"
                            size="sm"
                            onClick={() => onEdit(step)}
                            title="Edit step"
                        >
                            <IconEdit size={"2em"}/>
                        </ActionIcon>
                    </Box>
                    : <Box/>
                }
            </Box>

            {renderInstructionView()}
            {/* Step content - uses StepView directly */}
            {step.kind !== IProtocolStepKind.GROUP ? <Box className={classes.stepContent}>
                <StepView
                    key={step.id}
                    step={step}
                    workflow={workflow}
                    onChange={(updates) => onChange(step.id, updates as DeepPartial<IProtocolStep>)}
                />
            </Box> : <Box/>}
            {/* Events - use EventListView for standard rendering with warnings */}
            {events.length > 0 && (
                <Box className={classes.eventsContainer}>
                    <EventListView
                        events={events}
                        eventWarnings={eventWarnings}
                        timezone={timezone}
                        readonly={true}
                    />
                </Box>
            )}
            {/* Cancel/Update buttons for review_edit mode */}
            {isEditing && (
                <Box style={{display: "flex", justifyContent: "flex-end", marginTop: "8px"}}>
                    <Group justify="flex-end">
                        <Button variant="default" size="xs" onClick={() => onCancel(step.id)}>
                            Cancel
                        </Button>
                        <Button size="xs" onClick={() => onUpdate(step.id)}>
                            Update
                        </Button>
                    </Group>
                </Box>
            )}


        </Box>
    );
};